import React, { useEffect, useState } from "react";
import axios from "axios";
import { ScrollText, FileText, Download, ShieldCheck, BadgeCheck, Loader2 } from "lucide-react";
import { SCHOOL_INFO } from "@/lib/assets";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Agrements() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/site-content?type=document`)
      .then(({ data }) => setDocs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white" data-testid="page-agrements">
      {/* Hero */}
      <section className="relative bg-[#1E3A8A] text-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4"><ShieldCheck className="h-3 w-3" /><span>Agréments officiels</span></div>
          <h1 className="mazid-headline text-4xl sm:text-5xl lg:text-6xl mb-6 max-w-4xl">
            Un établissement <span className="text-[#FBBF24]">reconnu et agréé</span> par l&apos;État guinéen.
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl leading-relaxed">
            La confiance des familles repose sur la transparence. Retrouvez ici l&apos;ensemble de nos
            autorisations et documents officiels, consultables et téléchargeables librement.
          </p>
        </div>
      </section>

      {/* Arrêté ministériel — mise en avant permanente */}
      <section className="py-14 bg-[#F8FAFC] border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-white border-2 border-[#FBBF24] rounded-2xl p-8 flex flex-col sm:flex-row items-start gap-5 shadow-sm" data-testid="agrement-arrete-card">
            <div className="p-4 rounded-xl bg-[#1E3A8A]">
              <ScrollText className="h-8 w-8 text-[#FBBF24]" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="h-5 w-5 text-green-600" />
                <span className="text-xs font-bold uppercase tracking-widest text-green-700">Autorisation d&apos;ouverture</span>
              </div>
              <h2 className="font-heading text-lg md:text-lg font-extrabold text-[#1E3A8A]">
                Arrêté Ministériel N° A/2022/265/MEPU-A/SGG
              </h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Portant ouverture d&apos;un Établissement d&apos;Enseignement Privé, délivré par le Ministère de
                l&apos;Enseignement Pré-Universitaire et de l&apos;Alphabétisation de la République de Guinée.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents téléchargeables */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-lg font-extrabold text-[#1E3A8A] uppercase tracking-wide mb-6">
            Documents officiels téléchargeables
          </h2>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
          ) : docs.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-10 text-center" data-testid="agrements-empty">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">
                Les copies numériques de nos documents officiels (arrêté, RCCM, NIF) seront publiées ici très prochainement.
                <br />En attendant, elles restent consultables sur demande auprès de l&apos;administration : {SCHOOL_INFO.phones[0]}.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {docs.map((d) => (
                <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow" data-testid={`agrement-doc-${d.id}`}>
                  <div className="p-3 rounded-xl bg-red-50 flex-shrink-0">
                    <FileText className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-[#0F172A]">{d.title}</h3>
                    {d.reference && <p className="text-xs font-mono text-red-700 mt-0.5">N° {d.reference}</p>}
                    {d.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{d.description}</p>}
                    {d.media_url && (
                      <a
                        href={d.media_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-xs font-bold hover:bg-blue-900 transition-colors"
                        data-testid={`agrement-download-${d.id}`}
                      >
                        <Download className="h-3.5 w-3.5" /> Télécharger le document
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bandeau confiance */}
      <section className="py-14 bg-[#166534] text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <ShieldCheck className="h-9 w-9 mx-auto mb-3 text-[#FBBF24]" />
          <h2 className="font-heading text-lg font-extrabold mb-2">Votre confiance, notre engagement</h2>
          <p className="text-sm text-green-100 max-w-2xl mx-auto leading-relaxed">
            Les Écoles Mazid opèrent en pleine conformité avec la réglementation guinéenne de
            l&apos;enseignement privé. Pour toute vérification : {SCHOOL_INFO.email} · {SCHOOL_INFO.phones[0]}.
          </p>
        </div>
      </section>
    </div>
  );
}
